import React from 'react'
import { useTranslation } from 'react-i18next'
import { LoaderCircle, MoreHorizontal, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type { PhotoAction } from './usePhotoBulkActions'

/** A disabled button never fires hover events, so the hint sits on a wrapper. */
const Hint: React.FC<{ text?: string | null }> = ({ text, children }) =>
  text ? (
    <Tooltip>
      <TooltipTrigger asChild><span tabIndex={0}>{children}</span></TooltipTrigger>
      <TooltipContent>{text}</TooltipContent>
    </Tooltip>
  ) : (
    <>{children}</>
  )

/** The actions as menu items — the selection bar's "more" menu and the right-click menu. */
export const PhotoActionItems: React.FC<{ actions: PhotoAction[] }> = ({ actions }) => (
  <>
    {actions.map((a) => {
      const Icon = a.icon
      const label = (
        <>
          <Icon className="mr-2 h-4 w-4" />
          {a.label}
        </>
      )
      if (a.options) {
        return (
          <DropdownMenuSub key={a.key}>
            <DropdownMenuSubTrigger disabled={a.disabled} title={a.hint ?? undefined}>{label}</DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="max-h-72 overflow-y-auto">
              {a.options.map((opt) => (
                <DropdownMenuItem key={opt.key} onSelect={opt.onSelect}>{opt.label}</DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )
      }
      return (
        <DropdownMenuItem
          key={a.key}
          disabled={a.disabled}
          title={a.hint ?? undefined}
          className={cn(a.destructive && 'text-destructive focus:text-destructive')}
          onSelect={a.onSelect}
        >
          {label}
        </DropdownMenuItem>
      )
    })}
  </>
)

interface Props {
  count: number
  actions: PhotoAction[]
  loading: boolean
  onClear: () => void
}

/**
 * Freelo #33206294: the selection's actions sit at the top, where the toolbar was, like Doc Control's selection
 * actions. The timeline scrolls in its own box, so the bar stays in view however far down photos are picked.
 */
const PhotoSelectionBar: React.FC<Props> = ({ count, actions, loading, onClear }) => {
  const { t } = useTranslation()
  const primary = actions.filter((a) => a.primary && !a.destructive)
  const more = actions.filter((a) => !a.primary)
  const destructive = actions.filter((a) => a.primary && a.destructive)

  const renderButton = (a: PhotoAction) => {
    const Icon = a.icon
    const icon = loading && a.options ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />
    if (a.options) {
      return (
        <Hint key={a.key} text={a.hint}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8" disabled={a.disabled}>
                {icon}
                {a.label}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-72 overflow-y-auto">
              {a.options.map((opt) => (
                <DropdownMenuItem key={opt.key} onSelect={opt.onSelect}>{opt.label}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </Hint>
      )
    }
    return (
      <Hint key={a.key} text={a.hint}>
        <Button variant={a.destructive ? 'destructive' : 'outline'} size="sm" className="h-8" disabled={a.disabled} onClick={a.onSelect}>
          {icon}
          {a.label}
        </Button>
      </Hint>
    )
  }

  return (
    <TooltipProvider>
      <div className="flex flex-wrap items-center gap-2 py-2">
        <span className="px-1 text-sm font-medium">{t('Photos_selected', { count })}</span>
        <Button variant="ghost" size="sm" className="h-8" onClick={onClear} disabled={loading}>
          <X className="h-4 w-4" />
          {t('Common_clear_selection')}
        </Button>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {primary.map(renderButton)}
          {more.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 w-8 p-0" aria-label={t('Photos_more')}>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <PhotoActionItems actions={more} />
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {destructive.map(renderButton)}
        </div>
      </div>
    </TooltipProvider>
  )
}

export default PhotoSelectionBar
